import React, {useEffect, useReducer} from 'react';
import pt from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import axios from 'axios';
import {useLocalization} from '@epic-core/localization';
import TextField from '@material-ui/core/TextField';

const valid = /^https:\/\/[a-zA-Z0-9]*\.unrealengine\.com\/.*\.json$/;

const emptyOption = {value: '', label: ''};

const getOptions = async (dispatch, optionsUrl) => {
    dispatch({type: 'loading'});
    let data = [];
    try {
        const response = await axios.get(optionsUrl);
        const responseData = response.data;

        if (responseData && typeof responseData === 'string') {
            data = JSON.parse(responseData) || [];
        } else if (responseData && responseData.length && typeof responseData === 'object') {
            data = responseData;
        } else {
            console.error('MarketoSelect.getOptions() response was invalid json array');
            data = [];
        }
    } catch (e) {
        console.error('MarketoSelect.getOptions() failed to load and parse the options');
    }
    dispatch({type: 'options', payload: data || []});
};

const reducer = (state, {type, payload}) => {
    switch (type) {
        case 'options': {
            return {loading: false, optionArray: payload};
        }
        case 'loading': {
            return {loading: true, optionArray: []};
        }
        default:
            break;
    }

	return {loading: false, optionArray: []};
};

const getBooleanOptions = (getMessage) => {
    const yes = getMessage('com.epicgames.marketo.boolean.yes');
    const no = getMessage('com.epicgames.marketo.boolean.no');
    // option value better to be a string rather than true/false
    // due to that a "false" can result in a werid UI bug where option is considered inactive.
    const options = [
        {
            value: 'yes',
            label: yes
        },
        {
            value: 'no',
            label: no
        }
    ];
    return options;
};

const MarketoSelect = ({id, options, onSelectChange, mutators, isMulti, value, label, isBoolean, required, helperText, error}) => {
    const [{loading, optionArray}, dispatch] = useReducer(reducer, {});
    const getMessage = useLocalization();
    const booleanOptions = getBooleanOptions(getMessage);
    const [inputValue, setInputValue] = React.useState('');
    useEffect(() => {
        if (!isBoolean && (!loading || !optionArray || !optionArray.length) && options && valid.test(options)) {
            getOptions(dispatch, options);
        }
    }, [options]);

    let finalOptions = optionArray || [];
    //check for grouped options
    const flattenedOptions = [];
    finalOptions.forEach(option => {
        if (option.options && option.options.length) {
            option.options.forEach(innerOption => {
                innerOption.groupLabel = option.label;
                flattenedOptions.push(innerOption);
            });
        }
    });
    if (flattenedOptions.length) {
        finalOptions = flattenedOptions;
    }
    // if select-boolean component in cms, use yes/no bool options from messages.
    if (isBoolean) {
        finalOptions = booleanOptions;
    }
    const {setValue, setFieldData} = mutators;
    const finalOptionsWithEmptyOption = [emptyOption].concat(finalOptions || []);

    //Don't mark required if we have a good values in the autocomplete otherwise the browser marks the input as an error
    const textFieldValue = isMulti ? value : value && value.value;

    //keep the autocomplete value in sync with the field value in order to properly reset
    const autoCompleteProps = {};
    autoCompleteProps.value = isMulti ? value || [] : value || emptyOption;

    return (
        <Autocomplete
            {...autoCompleteProps}
            id={id}
            key={`select-${id}`}
            multiple={isMulti}
            blurOnSelect={!isMulti}
            filterOptions={(options, state) => {
                const finalOptions = [];
                const inputValueUpperCase = inputValue.toUpperCase();
                 options.forEach(option => {
                    if (option && option.value !== '' && (!inputValue ||  option.label.toUpperCase().startsWith(inputValueUpperCase) ) ) {
                        finalOptions.push(option);
                    }
                });
                return finalOptions;
            }}
            options={finalOptionsWithEmptyOption}
            getOptionLabel={(option) => option.label || ''}
            placeholder=""
            handleHomeEndKeys
            autoComplete
            autoHighlight
            clearOnEscape
            getOptionSelected={(option, value) => value.value === option.value}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            onChange={(e, selection) => {
                setFieldData(id, {touched: true});
                setValue(id, selection);
                if (onSelectChange && typeof onSelectChange === 'function') {
                    onSelectChange({id, selection, setValue, setFieldData});
                }
            }}
            onOpen={() => {
                setFieldData(id, {active: true});
            }}
            onClose={() => {
                setFieldData(id, {active: false, touched: true});
            }}
            groupBy={(option) => option.groupLabel}
            renderInput={(params) =>
                <TextField {...params}
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: 'new-password'
                    }}
                    label={label}
                    required={required && !(textFieldValue && textFieldValue.length)}
                    helperText={helperText}
                    error={error} />}
        />
    );
};

MarketoSelect.propTypes = {
    options: pt.string,
    id: pt.string,
    onSelectChange: pt.func,
    mutators: pt.object,
    isMulti: pt.bool,
    isBoolean: pt.bool,
    value: pt.any,
    label: pt.string,
    required: pt.bool,
    helperText: pt.string,
    error: pt.bool
};

export default MarketoSelect;
